// ============================================================
// ANSICHTSSACHE-AGENT – Live: was NICHT meldet
// ============================================================
// Zwei Sorten Schreibvorgaenge duerfen keine Auffrischung
// ausloesen:
//
// 1. PROTOKOLLE. Sie entstehen NEBEN der eigentlichen Arbeit
//    (jeder Zugriff, jede Pruefung). Frischte jeder Eintrag jede
//    offene Seite auf, und schriebe das Zeichnen einer Seite selbst
//    ins Protokoll, drehte sich die Anwendung im Kreis. Seit dem
//    26.09.2026 gehen sie trotzdem ueber die Leitung — aber nur als
//    Horcher-Ereignis (`PROTOKOLL_MODELLE` unten): auffrischen tun
//    allein die Ansichten, die ein Protokoll ZEIGEN und sich dafuer
//    mit `<LiveRefreshOn models={[…]} />` anmelden (Verlauf,
//    Aenderungsprotokoll, Prueflaeufe, Zugriffe). Vorher stand dort
//    alles still, was NUR ins Protokoll schrieb — ein Prueflauf,
//    eine Testmail, das Kuerzen der Zugriffe. Wer eine Seite
//    anmeldet, prueft vorher, dass ihr Zeichnen dieses Protokoll
//    nicht selbst beschreibt; sonst ist die Schleife zurueck.
// 2. DER CHAT. Er hat seinen eigenen Strom (`/api/chat/stream`),
//    der einzelne Nachrichten nachliefert, statt die ganze Seite
//    neu zu holen. Beides zusammen hiesse: jede getippte Zeile
//    laedt allen Anwesenden ihre Ansicht neu.
// ============================================================

/**
 * Die Protokolle (siehe oben, 1.): Horcher-Ereignis, keine
 * allgemeine Auffrischung. Das Pruefprotokoll der USt-IdNr.
 * (`VatCheck`) gehoert dazu — den Stammsatz traegt die `Company`,
 * die im selben Zug geschrieben wird und sich selbst meldet.
 */
export const PROTOKOLL_MODELLE = [
  "AuditLog",
  "AuditSeal",
  "AccessLog",
  "ComplianceCheck",
  "VatCheck",
  // Wer ein Geheimnis an einer Notiz angesehen hat — die Notiz zeigt
  // es und meldet sich dafuer an (`notizen/[notizId]/page.tsx`).
  "HouseNoteSecretAccess",
  // Der Stempelchip schreibt bei JEDEM Antippen und bei jedem Schritt
  // der Einrichtung mit (Zaehler, Zustand) — das soll nicht jede
  // offene Seite neu zeichnen. Das Register unter Einstellungen ›
  // Zeiterfassung meldet sich an (`stamp-chips-panel.tsx`).
  "StampChip",
] as const;

const STILLE_MODELLE = new Set([
  // Der Anmeldevorgang mit Passkey — den zeigt niemand an.
  "WebAuthnChallenge",
  // Verbundene Geraete: „zuletzt genutzt“ laeuft bei jedem Aufruf
  // eines Geraets mit und darf nicht jeden Reiter neu zeichnen. Die
  // Liste sieht nur die Person selbst, und die frischt nach ihrem
  // eigenen Verbinden oder Trennen selbst auf.
  "DeviceAccess",
  // Kalender-Abos und fremde Kalender: „zuletzt abgerufen“ und der
  // letzte Fehler laufen bei jedem Abruf mit. Beides sieht nur die
  // Person selbst unter Mein Konto › Kalender, und ihre eigenen
  // Handgriffe frischen die Seite selbst auf.
  "CalendarFeed",
  "ExternalCalendar",
  // Die Abschnitte einer Uhr: entstehen immer zusammen mit einer
  // Aenderung an der `Stopwatch`, die sich selbst meldet, und keine
  // Ansicht zeigt sie (gelesen nur beim Anlegen eines Zeitvorschlags).
  "StopwatchRun",
  // Der Lesestand an Kommentaren: geschrieben bei jedem Oeffnen einer
  // Aufgabe. Wer gelesen hat, frischt seine eigene Seite selbst auf
  // (`CommentThread`); alle anderen geht das nichts an.
  "CommentRead",
  // Der Chat spricht fuer sich selbst
  "ChatChannel",
  "ChatMembership",
  "ChatMessage",
  "ChatAttachment",
  "ChatMention",
  "ChatReaction",
  // Der Stand je Trello-Karte (`lib/trello/sync.ts`): jeder Abgleich
  // schreibt ihn neben die Aufgabe, die sich ohnehin selbst meldet.
  "TrelloCardLink",
  // Der Verbrauch der KI (`lib/anthropic.ts`): gezaehlt fuer das OPS
  // Center, gezeigt auf keiner Seite des Agents.
  "AiUsage",
  // Die Gespraeche mit dem Assistenten (`lib/assistent`): gespeichert
  // nach jeder Runde einer Antwort. Sie gehoeren einer Person, und
  // ihr Fenster bekommt den Stand ueber den eigenen Strom — jeder
  // andere Reiter im Haus haette sonst je Runde neu gezeichnet.
  "AssistantChat",
  // Die Elemente und Punkte eines Whiteboards: die Flaeche hat ihren
  // eigenen Kanal (`lib/whiteboard/kanal.ts`), der das Gesicherte
  // gleich mitbringt. Ueber diese Leitung hiesse jeder Zug einer
  // Notiz, dass jeder offene Reiter im Haus seine Seite neu holt.
  // Die Liste der Flaechen haengt an `Whiteboard`, das meldet sich.
  "WhiteboardItem",
  "WhiteboardVote",
]);

/** Schreibarten. Alles andere liest nur und meldet nichts. */
export const SCHREIBENDE_AKTIONEN = new Set([
  "create",
  "createMany",
  "createManyAndReturn",
  "update",
  "updateMany",
  "updateManyAndReturn",
  "upsert",
  "delete",
  "deleteMany",
]);

export function istStill(modell: string): boolean {
  return STILLE_MODELLE.has(modell);
}

/**
 * Modelle, die keine Seite auf dem Server zeichnet: die Meldungen der
 * Glocke und die Geraete fuer Web-Push. Gelesen werden sie nur ueber
 * `/api/notifications` und `/api/push` — von Ansichten, die sich mit
 * `useLive` selbst anmelden.
 *
 * Ihre Ereignisse gehen deshalb nur an diese Horcher und loesen kein
 * `router.refresh()` aus. Vorher zeichnete jede Meldung — und danach
 * noch einmal der Zeitstempel am Push-Geraet — jeden offenen Reiter
 * jedes Menschen im Haus neu, ohne dass sich darin etwas aenderte.
 *
 * Liest eine Server-Komponente eines dieser Modelle, gehoert es hier
 * wieder heraus — oder die Seite meldet sich mit `<LiveRefreshOn>`
 * dafuer an (so die Protokolle). Sonst steht sie still.
 */
// Die Startansichten liest nur der Filterkopf im Browser
// (`components/liste/saved-views.tsx`). `SavedView` selbst steht
// NICHT hier: angeheftete Auswertungen zeichnet die
// Berichte-Uebersicht auf dem Server als Kachel.
const LISTENER_ONLY_MODELS = new Set<string>([
  "Notification",
  "PushDevice",
  "SavedViewDefault",
  ...PROTOKOLL_MODELLE,
]);

export function isListenerOnly(modell: string): boolean {
  return LISTENER_ONLY_MODELS.has(modell);
}
